import { UseFormReturn } from 'react-hook-form';
import { addDays, subDays } from 'date-fns';

import { useQueryCheckPresetForUpdateIsShared } from '@hooks/kpis/useMutateCheckPresetForUpdateIsShared';
import { useQueryKpiSettings } from '@hooks/kpis/useQueryKpiSettings';

import { IDateInterval, IPreset } from '@interfaces/kpi/kpiCommon';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';

export const usePresetParametersLogic = ({
	formMethods,
	defaultFrom,
	maxDateInterval,
	presetValue,
}: {
	formMethods: UseFormReturn;
	defaultFrom: number;
	maxDateInterval: number;
	presetValue?: TSelectOptionType & IPreset;
}) => {
	const {
		vendor,
		isAutomaticGranularity,
		granularity,
		initTreeKpiData,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
	} = formMethods.watch();

	const isOranVendor = vendor?.value === EVendorsValues.oran;
	const from = formMethods.watch(EFormFields.from);
	const to = formMethods.watch(EFormFields.to);
	const dateSelectionMode: string | undefined = formMethods.watch(EFormFields.dateSelectionMode);
	const dynamicDateRange: TSelectOptionType<string> = formMethods.watch(EFormFields.dynamicDateRange);
	const startDateOffset: TSelectOptionType<number> = formMethods.watch(EFormFields.startDateOffset);
	const isShared = formMethods.watch(EFormFields.isShared);

	const { maxSelection } = useQueryKpiSettings(vendor?.value);

	const minDate = subDays(defaultFrom, maxDateInterval);

	const includeDateIntervalsFrom: IDateInterval[] | undefined = to
		? [{
			start: subDays(to, maxDateInterval),
			end: to,
		}]
		: undefined;

	const includeDateIntervalsTo: IDateInterval[] | undefined = from
		? [{
			start: from,
			end: addDays(from, maxDateInterval),
		}]
		: undefined;

	const { isPassName, isLoadingCheckPresetForUpdateIsShared } = useQueryCheckPresetForUpdateIsShared({
		id: presetValue?.id,
		name: presetValue?.name,
	});

	return {
		vendor,
		isAutomaticGranularity,
		granularity,
		initTreeKpiData,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
		isOranVendor,
		from,
		to,
		maxSelection,
		minDate,
		includeDateIntervalsFrom,
		includeDateIntervalsTo,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
		isShared,
		isPassName,
		isLoadingCheckPresetForUpdateIsShared,
	};
};
